import React from 'react';
import classnames from 'classnames';
import useResizeObserver from '@react-hook/resize-observer';
import { isChrome } from 'react-device-detect';

import styles from './CollapsePanel.module.css';
import { stateHooks } from '@nxte-nl/state-hooks';

export type ClientHeightEntry = {
  target: {
    clientHeight: number;
  };
};

export type CollapseChromePanelsOnLoad = {
  ref: React.RefObject<HTMLDivElement>;
  className: string;
  isChrome: boolean;
  isSearchTextFragment?: boolean;
  setChevronState: (value: boolean) => void;
  initialExpandedState?: boolean;
};

export type ToggleChromePanel = {
  ref: React.RefObject<HTMLDivElement>;
  toggle: () => void;
};

export type UseUpdateChevronParams = {
  chevronState: boolean;
  setChevron: React.Dispatch<React.SetStateAction<boolean>>;
};

export type UseUpdateFirefoxHiddenAttributeParams = {
  fireFoxRef: React.RefObject<HTMLDivElement> | null;
  chevron: boolean;
};

export type UseUpdateChromeStateParams = {
  isChromeState: boolean;
  setIsChromeState: React.Dispatch<React.SetStateAction<boolean>>;
};

export const hooks = {
  useGetStates(chevronState: boolean) {
    const [height, setHeight] = React.useState(0);
    const [isFirstTime, setIsFirstTime] = React.useState<boolean>(true);
    const [isChromeState, setIsChromeState] = React.useState<boolean>(isChrome);
    const [chevron, setChevron] = React.useState<boolean>(chevronState);
    return {
      height,
      setHeight,
      isFirstTime,
      setIsFirstTime,
      isChromeState,
      setIsChromeState,
      chevron,
      setChevron,
    }
  },
  useContentHeight(chevronState: boolean) {
    const {
      height,
      setHeight,
    } = hooks.useGetStates(chevronState)
    return {
      height,
      setHeight: React.useCallback(
        (e: ClientHeightEntry) => setHeight(e.target.clientHeight), [setHeight]),
    };
  },
  useCollapsedStyle(chevronState: boolean) {
    const ref = React.useRef<HTMLDivElement>(null);
    const fireFoxRef = React.useRef<HTMLDivElement>(null);
    const { height, setHeight } = hooks.useContentHeight(chevronState);

    useResizeObserver(ref, setHeight);
    useResizeObserver(fireFoxRef, setHeight);

    const style = { '--children-height': `${height}px` } as React.CSSProperties;

    return { ref, style, height, fireFoxRef };
  },
  useRemoveClassnameListenerCallback (
    currentRef: HTMLDivElement | null,
    className: string,
  ) {
    const removeClassnameListener = React.useCallback(() => {
      currentRef && currentRef.classList.remove(className)
    }, [className, currentRef]);
    return { removeClassnameListener };
  },

  useCollapseChromePanelsOnLoad(
    {
      ref,
      className,
      isChrome,
      isSearchTextFragment,
      setChevronState,
      initialExpandedState,
    }: CollapseChromePanelsOnLoad) {
    const currentRef = ref.current;
    const { 
      removeClassnameListener,
    } = hooks.useRemoveClassnameListenerCallback (currentRef, className);
    React.useEffect(() => {
      if (currentRef && isChrome && !initialExpandedState) {
        currentRef.addEventListener('beforematch', removeClassnameListener);
        currentRef.setAttribute('hidden', 'until-found');
        setChevronState(false);
      }
    }, [
      className,
      currentRef,
      isChrome,
      removeClassnameListener,
      ref,
      isSearchTextFragment,
      initialExpandedState,
      setChevronState,
    ]);
  },

  useToggleChromePanel({ ref, toggle }: ToggleChromePanel) {
    const toggleChromePanel = React.useCallback(() => {
      if (ref.current?.hasAttribute('hidden')) {
        ref.current.removeAttribute('hidden');
        toggle()
        return;
      }
      ref?.current?.setAttribute('hidden', 'until-found');
      toggle();
    }, [ref, toggle]);
    return { toggleChromePanel }
  },

  useUpdateChevron({
    setChevron,
    chevronState,
  }: UseUpdateChevronParams) {
    React.useEffect(() => {
      setChevron(chevronState)
    }, [chevronState, setChevron]);
  },

  useUpdateFirefoxHiddenAttribute({
    fireFoxRef,
    chevron,
  }: UseUpdateFirefoxHiddenAttributeParams) {
    React.useEffect(() => {
      if (fireFoxRef?.current) {
        chevron ? 
          fireFoxRef?.current.removeAttribute('hidden') :
          fireFoxRef?.current.setAttribute('hidden', 'true');
      }
    }, [chevron, fireFoxRef]);
  },

  useUpdateChromeState({
    isChromeState,
    setIsChromeState,
  }: UseUpdateChromeStateParams) {
    const prevChromeState = hooks.usePrevioustDataLoadedState(isChromeState);
    React.useEffect(() => {
      if (isChromeState !== prevChromeState) {
        setIsChromeState(isChrome)
      }
    }, [isChromeState, prevChromeState, setIsChromeState]);
  },
  usePrevioustDataLoadedState(flag: boolean) {
    const ref = React.useRef<boolean>(flag);
    React.useEffect(() => {
      ref.current = flag;
    }, [flag]);
    return ref.current;
  },
};

export type Props = {
  children: React.ReactNode;
  title: React.ReactNode;
  className?: string;
  onToggle: (expanded: boolean) => void;
  initialExpandedState?: boolean;
  isSearchTextFragment?: boolean;
  chevronState: boolean;
};

export const CollapsePanel = React.memo((props: Props) => {
  const {
    title,
    onToggle,
    children,
    className,
    initialExpandedState = false,
    chevronState,

  } = props;

  const {
    chevron,
    setChevron,
    isChromeState,
    setIsChromeState,
  } = hooks.useGetStates(chevronState);

  hooks.useUpdateChevron({
    setChevron,
    chevronState,
  });

  const { style, ref, fireFoxRef } = hooks.useCollapsedStyle(chevronState);

  hooks.useUpdateFirefoxHiddenAttribute({
    fireFoxRef,
    chevron,
  })

  const { value: expanded, toggle } = stateHooks.useBooleanState({
    initialState: initialExpandedState,
    onToggle,
    isControlledState: true,
  });

  hooks.useCollapseChromePanelsOnLoad({
    ref,
    isChrome: isChromeState,
    className: styles.collapsed,
    setChevronState: setChevron,
    initialExpandedState,
  });

  hooks.useUpdateChromeState({
    isChromeState,
    setIsChromeState,
  })

  const { toggleChromePanel } = hooks.useToggleChromePanel({
    ref,
    toggle,
  })

  if (isChromeState) {
    return (
      <div className={classnames(styles.collapsePanel, className)}>
        <div className={styles.header} onClick={toggleChromePanel}>
          {title}
        </div>
        <div
          ref={ref}
          style={style}
          className={classnames({
            [styles.collapsed]: !expanded,
          })}
        >
          {children}
        </div>
      </div>
    )
  }

  return (
    <div className={classnames(styles.collapsePanel, className)}>
      <div className={styles.header} onClick={toggle}>
        {title}
      </div>
      <div
        className={classnames(styles.children, {
          [styles.collapsed]: !expanded,
        })}
        ref={fireFoxRef}
        style={style}
      >
        {children}
      </div>
    </div>
  );
});

CollapsePanel.displayName = 'CollapsePanel';
